import './PuxEditorBar.scss'

import React, { useEffect, useRef } from 'react'

import PuxIcon from '../PuxIcon/PuxIcon'
import { usePuxEditor } from './PuxEditorContext'
import { useEditorPresence } from './useEditorPresence'

interface Props {
  isPreview?: boolean
  isAuthenticated?: boolean
  pageContentItemId?: string
}

export default function PuxEditorBar({
  isPreview = false,
  isAuthenticated = true,
  pageContentItemId = ``,
}: Props): JSX.Element {
  const { session, store, setToolbar } = usePuxEditor()
  const bar = useRef<HTMLElement>(null)
  const details = useRef<HTMLDetailsElement>(null)
  const collaboration =
    isAuthenticated && !isPreview && Boolean(pageContentItemId)
  const presence = useEditorPresence(pageContentItemId, collaboration)
  const activeCount = presence.editors.filter((editor) => editor.active).length
  const disabled = !session || session.loading || session.saving
  const cmsUrl = process.env.GATSBY_ORCHARD_API_URL
  let status = isPreview ? `Page preview` : `Select content to edit`
  if (!isAuthenticated)
    status = isPreview ? `Log in to preview` : `Log in to edit`
  if (session) status = `No changes`
  if (session?.dirty) status = `Unsaved changes`
  if (session?.saving) status = `Saving...`
  if (session?.loading) status = `Loading content...`
  if (session?.error) status = session.error
  let presenceLabel = `${activeCount} active editor${
    activeCount === 1 ? `` : `s`
  }`
  if (activeCount > 1) presenceLabel = `Multiple editors on this page`
  if (presence.status === `loading`) presenceLabel = `Loading editors...`
  if (presence.status === `error`) presenceLabel = `Editor activity unavailable`

  useEffect(() => {
    const updateHeight = () =>
      document.documentElement.style.setProperty(
        `--pux-editor-height`,
        `${bar.current?.offsetHeight ?? 0}px`
      )
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    if (bar.current) observer.observe(bar.current)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty(`--pux-editor-height`)
    }
  }, [])

  return (
    <>
      <div className='PuxEditor-spacer' aria-hidden='true' />
      <aside className='PuxEditor' aria-label='Pux Editor' ref={bar}>
        <div className='PuxEditor-brand'>
          Pux <span>Editor</span>
        </div>
        <span className='PuxEditor-mode'>{isPreview ? `Preview` : `Edit`}</span>
        <div className='PuxEditor-context'>
          {session && (
            <span className='PuxEditor-field' title={session.label}>
              {session.kind === `input` ? `Text` : `Rich text`} ·{` `}
              {session.label}
            </span>
          )}
          <span
            className={`PuxEditor-status${session?.error ? ` is-error` : ``}`}
            role='status'
          >
            {status}
          </span>
        </div>
        {session?.kind === `wysiwyg` && (
          <fieldset
            className='PuxEditor-formatting'
            disabled={disabled}
            aria-label='Text formatting'
          >
            <div key={session.id} ref={setToolbar} className='PuxEditor-quill'>
              <select
                className='ql-header'
                defaultValue=''
                aria-label='Text style'
              >
                <option value=''>Paragraph</option>
                {[1, 2, 3, 4, 5, 6].map((level) => (
                  <option key={level} value={level}>
                    Heading {level}
                  </option>
                ))}
              </select>
              <select
                className='ql-align'
                defaultValue=''
                aria-label='Text alignment'
              >
                <option value=''>Left</option>
                <option value='center'>Center</option>
                <option value='right'>Right</option>
                <option value='justify'>Justify</option>
              </select>
              <button
                type='button'
                className='ql-bold'
                aria-label='Bold'
                title='Bold'
              />
              <button
                type='button'
                className='ql-italic'
                aria-label='Italic'
                title='Italic'
              />
              <button
                type='button'
                className='ql-underline'
                aria-label='Underline'
                title='Underline'
              />
              <button
                type='button'
                className='ql-code-block'
                aria-label='Code block'
                title='Code block'
              />
              <button
                type='button'
                className='ql-list'
                value='ordered'
                aria-label='Numbered list'
                title='Numbered list'
              />
              <button
                type='button'
                className='ql-list'
                value='bullet'
                aria-label='Bullet list'
                title='Bullet list'
              />
            </div>
            <button
              type='button'
              className='Button Button--sm Button--ghost PuxEditor-button PuxEditor-link'
              title='Insert content link'
              aria-label='Insert content link'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => store.contentLink()}
            >
              <PuxIcon
                name='user-interface/link'
                size={16}
                color='current'
                decorative
              />
            </button>
          </fieldset>
        )}
        <div className='PuxEditor-actions'>
          {collaboration && (
            <details
              className='PuxEditor-details'
              ref={details}
              onKeyDown={(event) => {
                if (event.key === `Escape` && details.current) {
                  details.current.open = false
                  details.current.querySelector(`summary`)?.focus()
                }
              }}
            >
              <summary
                aria-label='Editors and page actions'
                title='Editors and page actions'
              >
                <span className='PuxEditor-avatars' aria-hidden='true'>
                  {presence.editors.slice(0, 3).map((editor) => (
                    <span
                      key={editor.userName}
                      className={`PuxEditor-avatar${
                        editor.active ? `` : ` is-inactive`
                      }`}
                    >
                      {editor.userName.slice(0, 2).toUpperCase()}
                    </span>
                  ))}
                  {presence.editors.length > 3 && (
                    <span className='PuxEditor-avatar'>
                      +{presence.editors.length - 3}
                    </span>
                  )}
                </span>
                {collaboration && presence.status === `error` && (
                  <span
                    className='PuxEditor-presenceError'
                    title='Editor activity unavailable'
                  >
                    !
                  </span>
                )}
                <PuxIcon
                  name='user-interface/menu-3'
                  size={16}
                  color='current'
                  decorative
                />
              </summary>
              <div className='PuxEditor-popover'>
                {collaboration && (
                  <>
                    <strong>{presenceLabel}</strong>
                    <ul>
                      {presence.editors.map((editor) => (
                        <li key={editor.userName}>
                          <span>
                            {editor.userName} ·{` `}
                            {editor.active ? `Active` : `Inactive`}
                          </span>
                          <small>
                            Last activity:{` `}
                            {editor.lastActive &&
                            !isNaN(Date.parse(editor.lastActive))
                              ? new Date(editor.lastActive).toLocaleString()
                              : `Unknown`}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {isAuthenticated && pageContentItemId && (
                  <a
                    className='Button Button--sm Button--ghost PuxEditor-mobileCms'
                    href={`${cmsUrl}/Admin/Contents/ContentItems/${pageContentItemId}/Edit`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Edit in CMS
                  </a>
                )}
              </div>
            </details>
          )}
          {collaboration && presence.status === `ready` && activeCount > 1 && (
            <span
              className='PuxEditor-collaborationWarning'
              role='status'
              title='Multiple editors are working on this page'
            >
              {activeCount} editors
            </span>
          )}
          {isAuthenticated && pageContentItemId && (
            <a
              className={`Button Button--sm Button--ghost PuxEditor-button${
                isPreview ? `` : ` PuxEditor-cms`
              }`}
              href={`${cmsUrl}/Admin/Contents/ContentItems/${pageContentItemId}/Edit`}
              target='_blank'
              rel='noopener noreferrer'
            >
              Edit in CMS
            </a>
          )}
          {!isAuthenticated && (
            <a
              className='Button Button--sm Button--primary PuxEditor-button'
              href={`${cmsUrl}/Login`}
              target='_blank'
              rel='noopener noreferrer'
            >
              Log in
            </a>
          )}
          {session && (
            <>
              <button
                type='button'
                className='Button Button--sm Button--border PuxEditor-button'
                disabled={session.saving}
                onClick={() => store.cancel()}
              >
                Cancel
              </button>
              <button
                type='button'
                className='Button Button--sm Button--primary PuxEditor-button PuxEditor-save'
                disabled={disabled || !session.dirty}
                onClick={() => void store.save()}
              >
                {session.saving ? `Saving...` : `Save`}
              </button>
            </>
          )}
        </div>
        {session?.error && (
          <div className='PuxEditor-mobileError' role='alert'>
            {session.error}
          </div>
        )}
      </aside>
    </>
  )
}
